import React, { FC, ReactElement, useContext } from 'react'
import { FormProvider, useForm, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { SelfProvisioning as SelfProvisioningLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import FormRow from '../../Common/Components/FormRow'
import { InputHelp } from '../../Common/Components/InputHelp/InputHelp'
import PasswordTextInput from '../../Common/Components/PasswordTextInput'
import { Select } from '../../Common/Components/Selects'
import { SelectOption } from '../../Common/Components/Selects/Types'
import TextInput from '../../Common/Components/TextInput'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { LanguageContext } from '../../Common/Contexts'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import {
    ENTERPRISE_ID_FIELD_NAME,
    PASSWORD_FIELD_NAME,
    SITE_INDUSTRY_FIELD_NAME,
    SITE_NAME_FIELD_NAME,
    USER_NAME_FIELD_NAME
} from '../Constants/FieldNames'
import { FooterActions } from '../Actions/FooterActions'
import { SelfProvisioningConfig, SelfProvisioningConfigContext } from '../Contexts/SelfProvisioningConfigContext'
import { SelfProvisioningContext, SelfProvisioningContextType } from '../Contexts/SelfProvisioningContext'
import { AutocompleteValues } from '../../../NonReact/constants/FormConstants'
import { SelfProvisioningInitalDataFormState } from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'

export const InitialDataEntry: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const { industries }: SelfProvisioningConfig = useContext(SelfProvisioningConfigContext)
    const {
        formState: [initalState, setFormState],
        nextStep
    }: SelfProvisioningContextType = useContext(SelfProvisioningContext)

    // Form context.
    const methods: UseFormMethods<SelfProvisioningInitalDataFormState> = useForm({
        defaultValues: initalState ?? {
            siteName: '',
            siteIndustry: '',
            enterpriseID: '',
            userName: '',
            password: ''
        },
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    const { handleSubmit }: UseFormMethods<SelfProvisioningInitalDataFormState> = methods

    // What to do when the user submits the form.
    const submitProvisioningData = (state: SelfProvisioningInitalDataFormState): void => {
        setFormState(state)
        nextStep()
    }

    return (
        <FormProvider {...methods}>
            {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
            <form onSubmit={handleSubmit(submitProvisioningData)} noValidate>
                <div className='initial-data-entry'>
                    <ValidationErrors name={SITE_NAME_FIELD_NAME}>
                        <FormRow>
                            <TextInput
                                labelLeft={language.get(SelfProvisioningLanguage.SiteNameLabel)}
                                inputName={SITE_NAME_FIELD_NAME}
                                rules={{
                                    required: {
                                        value: true,
                                        message: language.get(
                                            SelfProvisioningLanguage.SelfProvisioning_Validation_SiteNameRequired
                                        )
                                    }
                                }}
                            />
                        </FormRow>
                    </ValidationErrors>
                    <ValidationErrors name={SITE_INDUSTRY_FIELD_NAME}>
                        <FormRow>
                            <Select
                                label={language.get(SelfProvisioningLanguage.SiteIndustryVerticalLabel)}
                                name={SITE_INDUSTRY_FIELD_NAME}
                                rules={{
                                    required: {
                                        value: true,
                                        message: language.get(
                                            SelfProvisioningLanguage.SelfProvisioning_Validation_SiteNameRequired
                                        )
                                    }
                                }}
                                options={industries.map(
                                    (industry: string): SelectOption<string> => ({
                                        label: language.get(`Industries_${industry}`),
                                        value: industry
                                    })
                                )}
                            />
                        </FormRow>
                    </ValidationErrors>

                    <ValidationErrors name={ENTERPRISE_ID_FIELD_NAME}>
                        <FormRow>
                            <TextInput
                                labelLeft={language.get(SelfProvisioningLanguage.EnterpriseIDLabel)}
                                inputName={ENTERPRISE_ID_FIELD_NAME}
                                rules={{
                                    required: {
                                        value: true,
                                        message: language.get(
                                            SelfProvisioningLanguage.SelfProvisioning_Validation_EnterpriseIDRequired
                                        )
                                    }
                                }}
                            />
                        </FormRow>
                    </ValidationErrors>
                    <ValidationErrors name={USER_NAME_FIELD_NAME}>
                        <FormRow>
                            <InputHelp
                                text={LanguageUtils.formatLanguageEntry(
                                    language.get(SelfProvisioningLanguage.SelfProvisioning_Help_Username)
                                )}
                            >
                                <TextInput
                                    labelLeft={language.get(SelfProvisioningLanguage.UsernameLabel)}
                                    inputName={USER_NAME_FIELD_NAME}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: language.get(
                                                SelfProvisioningLanguage.SelfProvisioning_Validation_UsernameRequired
                                            )
                                        }
                                    }}
                                />
                            </InputHelp>
                        </FormRow>
                    </ValidationErrors>
                    <ValidationErrors name={PASSWORD_FIELD_NAME}>
                        <FormRow>
                            <PasswordTextInput
                                labelLeft={language.get(SelfProvisioningLanguage.PasswordLabel)}
                                inputName={PASSWORD_FIELD_NAME}
                                isRandomiserEnabled={false}
                                isPasswordCheckerEnabled={false}
                                autoComplete={AutocompleteValues.NEW_PASSWORD}
                                rules={{
                                    required: {
                                        value: true,
                                        message: language.get(
                                            SelfProvisioningLanguage.SelfProvisioning_Validation_PasswordRequired
                                        )
                                    }
                                }}
                            />
                        </FormRow>
                    </ValidationErrors>
                </div>

                <FooterActions
                    showRightSideButton
                    rightSideButtonType='submit'
                    rightSideButtonLabel={language.get(SelfProvisioningLanguage.SelfProvisioning_FooterActions_Connect)}
                />
            </form>
        </FormProvider>
    )
}
